import fetcher from "@onair/utils/fetch-api";
import { UserRole } from "@onair/types/enums";
import {
	CountType,
	Pagination,
	FindManyFilter,
	FindUniqueFilter,
} from "@onair/types/extends";
import {
	UserWork,
	HostProfile,
	HostCategory,
	UserEducation,
	UserAchievement,
} from "@onair/types";

import { useAuthStore } from "../auth";
import { User } from "@onair/repositories";

export const actionGetCategory = async (
	slug: string,
	filter?: FindUniqueFilter<HostCategory>,
): Promise<HostCategory> => {
	const data = await fetcher<HostCategory>(`/host-categories/${slug}`, {
		data: {
			filter: JSON.stringify(filter),
		},
	});

	return data;
};

export const actionGetCategoryList = async (
	filter?: FindManyFilter<HostCategory>,
): Promise<Pagination<HostCategory>> => {
	const data = await fetcher<Pagination<HostCategory>>("/host-categories", {
		data: {
			filter: JSON.stringify(filter),
		},
	});

	return data;
};

export const actionUpdateHostOnCategory = async (
	payload: { categoryId: string; label: string }[],
): Promise<User> => {
	const data = await fetcher<Promise<User>>("/users/user-on-category", {
		method: "PATCH",
		data: payload,
	});

	return data;
};

export const actionGetHostProfile = async (
	username: string,
	filter?: FindUniqueFilter<CountType<User>>,
) => {
	const data = await fetcher<CountType<User>>(`/users/${username}`, {
		data: {
			filter: JSON.stringify(filter),
		},
	});

	return data;
};

export const actionGetHostList = async (
	filter?: FindManyFilter<User>,
): Promise<Pagination<User>> => {
	const data = await fetcher<Pagination<User>>("/users", {
		data: {
			filter: JSON.stringify({
				...filter,
				where: { ...filter?.where, role: UserRole.HOST },
			}),
		},
	});

	return data;
};

interface CreateHostData {
	phone: string;
	code: {
		otp: string;
		verificationKey: string;
		check: string;
	};
	email: string;
	fullName: string;
	categories: {
		value: string;
		label: string;
	}[];
	linkReferences?: string[];
	businesses: string[];
	registrationTopic?: string;
	fromAddress?: string;
	experience?: number;
	workAddress?: string;
	job?: string;
}

export const actionCreateHost = async (payload: CreateHostData) => {
	const data = await fetcher<{ success: boolean }>("/users", {
		method: "POST",
		data: payload,
	});

	return data;
};

export const actionGetHostRankings = async () => {
	const data = await fetcher<Partial<User & { amount: number }>[]>(
		"/users/host-rankings",
	);

	return data;
};

export const actionUpdateHostProfileAbout = async (payload: {
	intro: string;
	introPhotos: any[] | string[] | File[];
}) => {
	const data = await fetcher<User>("auth/update-profile", {
		method: "PATCH",
		data: payload,
	});

	return data;
};

export const actionUpdateHostProfileSkills = async (payload: {
	skills: string[];
}) => {
	const data = await fetcher<User>("auth/update-profile", {
		method: "PATCH",
		data: payload,
	});

	return data;
};

export const actionUpdateHostProfileKnowledge = async (payload: {
	knowledge: string[];
}) => {
	const data = await fetcher<User>("auth/update-profile", {
		method: "PATCH",
		data: payload,
	});

	return data;
};

export const actionUpdateHostProfileWork = async (payload: UserWork) => {
	const data = await fetcher<User>("/users/works", {
		method: "POST",
		data: payload,
	});

	return data;
};

export const actionUpdateHostProfileEducation = async (
	payload: UserEducation,
) => {
	const data = await fetcher<User>("/users/educations", {
		method: "POST",
		data: payload,
	});

	return data;
};

export const actionUpdateHostProfileAchievememts = async (
	payload: UserAchievement,
) => {
	const data = await fetcher<User>("/users/achievements", {
		method: "POST",
		data: payload,
	});

	return data;
};

export const actionCreateHostBankAccount = async (
	payload: {
		default: boolean;
		accountNumber: string;
		accountHolderName: string;
		bankName: string;
	}[],
) => {
	const res = await fetcher<User>("/users/update-bank-account", {
		method: "POST",
		data: payload,
	});

	useAuthStore.setState((old) => ({ ...old, ...res }));

	return res;
};

export const actionDeleteHostProfileWork = async (id: string) => {
	const data = await fetcher<UserWork>(`/users/works/${id}`, {
		method: "DELETE",
	});

	return data;
};

export const actionDeleteHostProfileEducation = async (id: string) => {
	const data = await fetcher<UserEducation>(`/users/educations/${id}`, {
		method: "DELETE",
	});

	return data;
};

export const actionDeleteHostProfileAchievement = async (id: string) => {
	const data = await fetcher<UserAchievement>(`/users/achievements/${id}`, {
		method: "DELETE",
	});

	return data;
};

type HostProfileData = Pick<
	HostProfile,
	| "invoiceEmail"
	| "typeOfActivity"
	| "docs"
	| "contactAddress"
	| "organizationName"
	| "organizationTaxNumber"
	| "personalTaxNumber"
	| "contactCity"
	| "contactDistrict"
	| "contactWard"
	| "typeOfId"
	| "idNumber"
	| "imageHandheldId"
	| "imageIdBack"
	| "imageIdFront"
	| "nameInId"
>;

export const actionRegisterHost = async (payload: HostProfileData) => {
	const data = await fetcher<HostProfile>("/users/register-host", {
		method: "POST",
		data: payload,
	});
	return data;
};

export const actionReSubmitRegisterHost = async (payload: HostProfileData) => {
	const data = await fetcher<HostProfile>("/users/resubmit-host", {
		method: "PATCH",
		data: payload,
	});
	return data;
};
